import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { theme } from "@polifonia/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";
import type { LineAdsErrorMessage } from "@/pages/applications/pages/lineAdsIntegration/useLineAdsErrorMessage";
import { useLineAdsErrorMessage } from "@/pages/applications/pages/lineAdsIntegration/useLineAdsErrorMessage";

interface ConnectLineAdsGroupModalProps {
  open: boolean;
  onClose: () => void;
  onConnected: () => void;
}

type TextFieldChangeHandler = React.ComponentProps<
  typeof TextField
>["onChange"];

const emptyForm = { externalGroupId: "", accessKey: "", secretKey: "" };

export const ConnectLineAdsGroupModal: React.FC<ConnectLineAdsGroupModalProps> =
  function ConnectLineAdsGroupModal({ open, onClose, onConnected }) {
    const { t } = useTranslation();
    const getLineAdsErrorMessage = useLineAdsErrorMessage();
    const [form, setForm] = useState(emptyForm);
    const [apiError, setApiError] = useState<LineAdsErrorMessage | null>(null);

    const registerGroupMutation = interlude.lineAds.useRegisterGroup(
      undefined,
      {
        onSuccess: onConnected,
        onError(error) {
          setApiError(getLineAdsErrorMessage(error));
        },
      },
    );

    const submitting = registerGroupMutation.isPending;
    const trimmed = {
      externalGroupId: form.externalGroupId.trim(),
      accessKey: form.accessKey.trim(),
      secretKey: form.secretKey.trim(),
    };
    const canSubmit =
      !submitting && Object.values(trimmed).every((value) => value !== "");

    const handleChange = useHandler<NonNullable<TextFieldChangeHandler>>(
      (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setApiError(null);
      },
    );
    const handleClose = useHandler(() => {
      if (submitting) return;
      onClose();
    });
    const handleSubmit = useHandler(() => {
      if (!canSubmit) return;
      setApiError(null);
      registerGroupMutation.mutate(trimmed);
    });

    const fields = [
      {
        name: "externalGroupId",
        label: t("applications.lineAds.connectModal.groupId", "Group ID"),
        isSecret: false,
      },
      {
        name: "accessKey",
        label: t(
          "applications.lineAds.connectModal.accessKey",
          "API access key",
        ),
        isSecret: false,
      },
      {
        name: "secretKey",
        label: t(
          "applications.lineAds.connectModal.secretKey",
          "API secret key",
        ),
        isSecret: true,
      },
    ] as const;

    return (
      <Modal
        open={open}
        handleClose={handleClose}
        title={t("applications.lineAds.connectModal.title", "Connect LINE Ads")}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button variant="plain" onClick={handleClose} disabled={submitting}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
            >
              {t("common.connect")}
            </Button>
          </Stack>
        }
      >
        <Stack gap="16px">
          {fields.map(({ name, label, isSecret }) => (
            <Box key={name} sx={sharedStyles.formSection}>
              <Box component="label" htmlFor={name} sx={sharedStyles.label}>
                <Typography
                  variant="body"
                  as="span"
                  fontWeight={500}
                  color={theme.colors.staticFgBody}
                >
                  {label}
                </Typography>
                <Box component="span" sx={sharedStyles.required}>
                  *
                </Box>
              </Box>
              <TextField
                id={name}
                name={name}
                type={isSecret ? "password" : "text"}
                value={form[name]}
                onChange={handleChange}
                disabled={submitting}
                autoComplete={isSecret ? "new-password" : "off"}
                fullWidth
              />
            </Box>
          ))}
          {apiError ? (
            <InfoBox
              variant="error"
              description={
                <Stack gap="4px">
                  <Typography variant="body" color="inherit">
                    {apiError.message}
                  </Typography>
                  {apiError.requestFormUrl ? (
                    <ExternalLink href={apiError.requestFormUrl} trailingIcon>
                      {t(
                        "applications.lineAds.error.requestFormLinkText",
                        "Request Data Provider API permission",
                      )}
                    </ExternalLink>
                  ) : null}
                </Stack>
              }
            />
          ) : null}
        </Stack>
      </Modal>
    );
  };
